import React, { useEffect, useMemo, useRef, useState } from "react";
import { PrayerTimes } from "../../types/PrayerTypes";
import "./SimpleUpcomingSalahChangesSlideshow.css";
import { ScreenData } from "../../types/ScreenTypes";
import { alignTimetablesToToday } from "../../utils/alignTimetablesToToday";
import { readableColour } from "../../utils/colourContrast";

interface SimpleUpcomingSalahChangesSlideshowProps {
  timetables: PrayerTimes[]; // Array of 7 days of prayer times
  customizedNames: Record<string, string>;
  duration: number;
  onComplete: () => void;
  prayerTimes: PrayerTimes;
  tomorrowPrayerTimes: PrayerTimes | null;
  // Add screenData to access background settings and salahChangesOrder
  screenData: ScreenData;
  // Add these new props for previous Friday's Jummah times
  prevFridayJummah1: string | null;
  prevFridayJummah2: string | null;
  prevFridayJummah3: string | null;
  prevFridayJummah4: string | null;
  // Custom colors for theming
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  widgetBackgroundColor?: string;
  // Container dimensions for responsive behavior
  width?: number;
  height?: number;
  // Progress tracking props
  currentIndex?: number;
  totalItems?: number;
  elapsedTime?: number;
  totalDuration?: number;
  // Restart trigger to reset the component without unmounting
  restartTrigger?: number;
  // Behavior controls
  hideWhenNoChanges?: boolean;
  onNoChanges?: () => void;
  // Filter to show only a specific prayer's changes (e.g. "JamDhuhr")
  filterPrayer?: string;
}

// Store the original Date object for correct sorting
interface PrayerChange {
  prayer: string;
  date: string | null;
  time: string | null;
  sortDate?: Date; // Add this property for sorting
}

// First let's create a new interface for our grouped format
interface GroupedPrayerChanges {
  date: string;
  displayDate: string;
  changes: Array<{
    prayer: string;
    time: string;
  }>;
}

const SimpleUpcomingSalahChangesSlideshow: React.FC<
  SimpleUpcomingSalahChangesSlideshowProps
> = ({
  timetables: rawTimetables,
  customizedNames,
  duration,
  onComplete,
  prayerTimes,
  tomorrowPrayerTimes,
  screenData,
  prevFridayJummah1,
  prevFridayJummah2,
  prevFridayJummah3,
  prevFridayJummah4,
  primaryColor = "#F5A623", // Warm Amber/Gold
  secondaryColor = "#FF8C42", // Soft Orange
  accentColor = "#10B981", // Emerald Green
  widgetBackgroundColor = "#0F172A", // Rich Dark (Slate 900)
  width = 300,
  height = 400,
  currentIndex,
  totalItems,
  elapsedTime,
  totalDuration,
  restartTrigger = 0,
  hideWhenNoChanges = false,
  onNoChanges,
  filterPrayer,
}) => {
  // Auto-advance after specified duration - Using a ref to prevent resetting timer on re-renders
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const hasCompletedRef = useRef<boolean>(false);
  const onCompleteCallbackRef = useRef(onComplete);
  const noChangesHandledRef = useRef(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [forceGroupedColumns, setForceGroupedColumns] = useState(false);
  const [fitScale, setFitScale] = useState(1);

  const timetables = alignTimetablesToToday(rawTimetables, new Date());

  // Update the callback ref when onComplete changes
  useEffect(() => {
    onCompleteCallbackRef.current = onComplete;
  }, [onComplete]);

  // Reset when restartTrigger changes
  useEffect(() => {
    // Restart trigger changed — reset component
    hasCompletedRef.current = false;
    noChangesHandledRef.current = false;

    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, [restartTrigger]);

  useEffect(() => {
    // Only set the timer if we haven't completed yet
    if (!hasCompletedRef.current) {
      // Setting timer for changes slideshow
      // Clear any existing timer first
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      // Set a new timer
      timerRef.current = setTimeout(() => {
        if (!hasCompletedRef.current) {
          hasCompletedRef.current = true;
          onCompleteCallbackRef.current();
        }
      }, duration * 1000);
    }

    return () => {
      // Only log cleanup if timer exists
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    // ✅ FIX: Only depend on duration, not onComplete (use ref instead)
  }, [duration]);

  // Find upcoming changes for each prayer - memoized for performance
  const upcomingChanges = useMemo(():
    | PrayerChange[]
    | GroupedPrayerChanges[] => {
    // Helper function to format dates for compact display
    const formatDate = (date: Date): string => {
      // Abbreviated day names
      const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];

      const dayOfWeek = days[date.getDay()];
      const day = date.getDate();
      const month = months[date.getMonth()];

      // Check if the date is tomorrow
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);

      const isTomorrow =
        date.getDate() === tomorrow.getDate() &&
        date.getMonth() === tomorrow.getMonth() &&
        date.getFullYear() === tomorrow.getFullYear();

      // If it's tomorrow, return "Tomorrow"
      if (isTomorrow) {
        return "Tomorrow";
      }

      // For compact version, use shorter format
      return `${dayOfWeek} ${day} ${month}`;
    };

    // Helper function to check if a date is a Friday
    const isFriday = (date: Date): boolean => date.getDay() === 5;

    // Azure Best Practice: Map prayer keys to their corresponding customizedNames property
    const allPrayers = [
      { key: "JamFajr", nameKey: "fajr" },
      { key: "JamDhuhr", nameKey: "dhuhr" },
      { key: "JamAsar", nameKey: "asar" },
      { key: "JamEsha", nameKey: "isha" },
      { key: "Jummah1", nameKey: "jummah" },
      { key: "Jummah2", nameKey: "jummah" },
      { key: "Jummah3", nameKey: "jummah" },
      { key: "Jummah4", nameKey: "jummah" },
    ];

    // If filterPrayer is set, only process that specific prayer key
    const prayers = filterPrayer
      ? allPrayers.filter((p) => p.key === filterPrayer)
      : allPrayers;

    const today = new Date();
    const changes: PrayerChange[] = [];

    prayers.forEach(({ key, nameKey }) => {
      if (!timetables || timetables.length < 1) {
        return;
      }

      let referenceTime: string | undefined;
      let referenceIndex = 0;

      // Special handling for JamDhuhr: ignore Fridays for both reference and change detection
      if (key === "JamDhuhr") {
        // ...existing code...
        let lastNonFridayTime: string | undefined;
        let lastNonFridayIndex = -1;
        for (let i = 0; i < timetables.length; i++) {
          const checkDate = new Date(today);
          checkDate.setDate(today.getDate() + i);
          if (!isFriday(checkDate)) {
            lastNonFridayTime = timetables[i][
              key as keyof PrayerTimes
            ] as string;
            lastNonFridayIndex = i;
            break;
          }
        }
        for (let i = lastNonFridayIndex + 1; i < timetables.length; i++) {
          const changeDate = new Date(today);
          changeDate.setDate(today.getDate() + i);
          if (isFriday(changeDate)) continue;
          if (
            changeDate.getDay() === 6 &&
            timetables[i][key as keyof PrayerTimes] ===
              timetables[i - 1][key as keyof PrayerTimes] &&
            isFriday(
              new Date(
                today.getFullYear(),
                today.getMonth(),
                today.getDate() + i - 1,
              ),
            )
          ) {
            continue;
          }
          const nextDayTime = timetables[i][key as keyof PrayerTimes] as string;
          if (
            !nextDayTime ||
            nextDayTime === lastNonFridayTime ||
            nextDayTime === "--:--"
          ) {
            continue;
          }
          changes.push({
            prayer: customizedNames.dhuhr || "Dhuhr",
            date: formatDate(changeDate),
            time: nextDayTime,
            sortDate: changeDate,
          });
          break;
        }
        return;
      }

      // Special handling for Jummah prayers
      if (key.startsWith("Jummah")) {
        const jummahNumber = key.replace("Jummah", "");
        const currentJummahTime = prayerTimes?.[key as keyof PrayerTimes];

        let prevFridayTime = null;
        if (key === "Jummah1") prevFridayTime = prevFridayJummah1;
        else if (key === "Jummah2") prevFridayTime = prevFridayJummah2;
        else if (key === "Jummah3") prevFridayTime = prevFridayJummah3;
        else if (key === "Jummah4") prevFridayTime = prevFridayJummah4;

        // Only show Jummah as a change if we have valid previous data AND it has actually changed
        if (
          currentJummahTime &&
          currentJummahTime !== "--:--" &&
          prevFridayTime &&
          prevFridayTime !== "--:--" &&
          prevFridayTime !== currentJummahTime
        ) {
          const prayerName =
            jummahNumber === "1"
              ? customizedNames.jummah || "Jumu'ah"
              : `${customizedNames.jummah || "Jumu'ah"} ${jummahNumber}`;

          // Get this Friday's date
          const thisFriday = new Date(today);
          const daysUntilFriday = (5 - today.getDay() + 7) % 7;
          thisFriday.setDate(today.getDate() + daysUntilFriday);

          changes.push({
            prayer: prayerName,
            date: formatDate(thisFriday),
            time: currentJummahTime as string,
            sortDate: thisFriday,
          });
        } else {
        }
        return;
      }

      // For other prayers, use the first day as reference
      referenceTime = timetables[0][key as keyof PrayerTimes] as string;
      referenceIndex = 0;

      let changeFound = false;
      for (let i = referenceIndex + 1; i < timetables.length; i++) {
        const nextDayTime = timetables[i][key as keyof PrayerTimes] as string;
        const changeDate = new Date(today);
        changeDate.setDate(today.getDate() + i);

        // Skip if no change or empty value
        if (
          !nextDayTime ||
          nextDayTime === referenceTime ||
          nextDayTime === "--:--"
        ) {
          continue;
        }

        let prayerName = "";
        if (key.startsWith("Jummah")) {
          const jummahNumber = key.replace("Jummah", "");
          prayerName =
            jummahNumber === "1"
              ? customizedNames.jummah || "Jumu'ah"
              : `${customizedNames.jummah || "Jumu'ah"} ${jummahNumber}`;
        } else {
          prayerName = customizedNames[nameKey]
            ? customizedNames[nameKey]
            : nameKey.charAt(0).toUpperCase() + nameKey.slice(1);
        }

        changes.push({
          prayer: prayerName,
          date: formatDate(changeDate),
          time: nextDayTime,
          sortDate: changeDate,
        });

        changeFound = true;
        break;
      }
    });

    // After getting all changes, check how to present them
    if (screenData.salahChangesOrder === "SalahOrder") {
      // Define a specific prayer order
      const prayerOrder = {
        [customizedNames.fajr || "Fajr"]: 1,
        [customizedNames.dhuhr || "Dhuhr"]: 2,
        [customizedNames.asar || "Asar"]: 3,
        [customizedNames.maghrib || "Maghrib"]: 4,
        [customizedNames.isha || "Isha"]: 5,
        [customizedNames.jummah || "Jumu'ah"]: 6,
      };

      // Add Jummah number-specific entries
      const jummahBase = customizedNames.jummah || "Jumu'ah";
      prayerOrder[jummahBase] = 6;
      prayerOrder[`${jummahBase} 1`] = 6;
      prayerOrder[`${jummahBase} 2`] = 7;
      prayerOrder[`${jummahBase} 3`] = 8;
      prayerOrder[`${jummahBase} 4`] = 9;

      // Sort by the prayer order, then by date
      changes.sort((a, b) => {
        const aIsJummah = a.prayer.includes(jummahBase);
        const bIsJummah = b.prayer.includes(jummahBase);

        if (aIsJummah && bIsJummah) {
          const aMatch = a.prayer.match(/\d+/);
          const bMatch = b.prayer.match(/\d+/);
          const aNum = aMatch ? parseInt(aMatch[0]) : 1;
          const bNum = bMatch ? parseInt(bMatch[0]) : 1;
          if (aNum !== bNum) return aNum - bNum;
          return compareDateAndTime(a, b);
        }

        const orderA = prayerOrder[a.prayer] || 999;
        const orderB = prayerOrder[b.prayer] || 999;
        const prayerComparison = orderA - orderB;
        if (prayerComparison !== 0) return prayerComparison;
        return compareDateAndTime(a, b);
      });

      return changes;
    } else {
      // Group by date when in DateOrder mode
      changes.sort((a, b) => compareDateAndTime(a, b));
      const groupedChanges: GroupedPrayerChanges[] = [];
      const dateMap: Record<string, GroupedPrayerChanges> = {};

      changes.forEach((change) => {
        const dateKey = change.sortDate!.toDateString();
        if (!dateMap[dateKey]) {
          dateMap[dateKey] = {
            date: dateKey,
            displayDate: change.date!,
            changes: [],
          };
          groupedChanges.push(dateMap[dateKey]);
        }
        dateMap[dateKey].changes.push({
          prayer: change.prayer,
          time: change.time!,
        });
      });

      return groupedChanges;
    }

    function compareDateAndTime(a: PrayerChange, b: PrayerChange): number {
      if (a.sortDate && b.sortDate) {
        const dateDiff = a.sortDate.getTime() - b.sortDate.getTime();
        if (dateDiff !== 0) return dateDiff;
      }
      const timeA = a.time || "00:00";
      const timeB = b.time || "00:00";
      const [hoursA, minutesA] = timeA.split(":").map(Number);
      const [hoursB, minutesB] = timeB.split(":").map(Number);
      if (hoursA !== hoursB) return hoursA - hoursB;
      return minutesA - minutesB;
    }
  }, [
    timetables,
    prayerTimes,
    customizedNames,
    prevFridayJummah1,
    prevFridayJummah2,
    prevFridayJummah3,
    prevFridayJummah4,
    screenData.salahChangesOrder,
    filterPrayer,
  ]);

  useEffect(() => {
    if (
      hideWhenNoChanges &&
      upcomingChanges.length === 0 &&
      !hasCompletedRef.current
    ) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      hasCompletedRef.current = true;
      if (!noChangesHandledRef.current) {
        noChangesHandledRef.current = true;
        onNoChanges?.();
      }
      onCompleteCallbackRef.current();
    }
  }, [hideWhenNoChanges, onNoChanges, upcomingChanges.length]);

  // Modify the formatDate function to use abbreviated day names
  const formatDate = (date: Date): string => {
    // Abbreviated day names
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const dayOfWeek = days[date.getDay()];
    const day = date.getDate();
    const month = months[date.getMonth()];

    // Check if the date is tomorrow
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    const isTomorrow =
      date.getDate() === tomorrow.getDate() &&
      date.getMonth() === tomorrow.getMonth() &&
      date.getFullYear() === tomorrow.getFullYear();

    // If it's tomorrow, return "Tomorrow"
    if (isTomorrow) {
      return "Tomorrow";
    }

    // For compact version, use shorter format
    return `${dayOfWeek} ${day} ${month}`;
  };

  // Get full day name for header display
  const getFullDayName = (date: Date): string => {
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    return days[date.getDay()];
  };

  // Get short date format for header (like "Sun 25 Jan")
  const getShortDate = (date: Date): string => {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
  };

  // Check if date is tomorrow
  const isTomorrow = (date: Date): boolean => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    return (
      date.getDate() === tomorrow.getDate() &&
      date.getMonth() === tomorrow.getMonth() &&
      date.getFullYear() === tomorrow.getFullYear()
    );
  };

  // Update the formatTime function to remove the hair space
  const formatTime = (timeStr: string): string => {
    if (!timeStr) return "";

    // If it already contains AM/PM, return as is
    if (timeStr.includes("AM") || timeStr.includes("PM")) {
      return timeStr;
    }

    // Parse 24-hour format and convert to 12-hour
    const [hours, minutes] = timeStr.split(":").map(Number);
    const period = hours >= 12 ? "PM" : "AM";
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${displayHours}:${minutes.toString().padStart(2, "0")}${period}`;
  };

  // Update formatPrayerTime function for compact display
  const formatPrayerTime = (time: string | null): React.ReactNode => {
    if (!time) return "—";

    const formattedTime = formatTime(time);
    if (!formattedTime.includes("AM") && !formattedTime.includes("PM")) {
      return formattedTime;
    }

    // Split time and period for styling
    const timeMatch = formattedTime.match(/^(\d{1,2}:\d{2})(AM|PM)$/);
    if (timeMatch) {
      const [, timePart, periodPart] = timeMatch;
      return (
        <>
          {timePart}
          <span className="period">{periodPart}</span>
        </>
      );
    }

    return formattedTime;
  };

  // Check if we have grouped data
  const isGroupedData =
    upcomingChanges.length > 0 && "changes" in upcomingChanges[0];

  const groupedChanges = isGroupedData
    ? (upcomingChanges as GroupedPrayerChanges[])
    : [];
  const prayerChanges = !isGroupedData
    ? (upcomingChanges as PrayerChange[])
    : [];

  const groupedDateCount = groupedChanges.length;
  const groupedItemCount = groupedChanges.reduce(
    (count, group) => count + group.changes.length,
    0,
  );

  // Fit strategy: render single-column, then the measurement effect below
  // switches to grouped columns and/or scales the list down if it overflows.
  const isLandscapeLike = width > height;
  const canUseColumns = isGroupedData && isLandscapeLike && groupedDateCount > 1;
  const useGroupedColumns = canUseColumns && forceGroupedColumns;

  useEffect(() => {
    // Reset measured fit when geometry/content conditions change.
    setForceGroupedColumns(false);
    setFitScale(1);
  }, [
    width,
    height,
    groupedDateCount,
    groupedItemCount,
    isGroupedData,
    screenData?.salahChangesOrder,
  ]);

  // Match UpcomingSalahChangesSlideshow theme variable contract for consistency.
  const resolvedPrimaryColor = screenData.primaryColor || primaryColor;
  const resolvedSecondaryColor = screenData.secondaryColor || secondaryColor;
  const resolvedAccentColor = screenData.accentColor || accentColor;
  const resolvedWidgetBackgroundColor =
    screenData.widgetBackgroundColor || widgetBackgroundColor;
  const resolvedHeadingTextColor = screenData.headingTextColor || "#FFFFFF";

  // Custom CSS variables for theming - use screenData colors with fallbacks
  const customStyles = {
    "--theme-primary-color": resolvedPrimaryColor,
    "--theme-secondary-color": resolvedSecondaryColor,
    "--theme-accent-color": resolvedAccentColor,
    "--compact-primary-color": resolvedPrimaryColor,
    "--compact-secondary-color": resolvedSecondaryColor,
    "--compact-accent-color": resolvedAccentColor,
    "--compact-background-color": resolvedWidgetBackgroundColor,
    "--heading-text-color": resolvedHeadingTextColor,
    // Only overridden when the badge text would be nearly invisible on the badge
    "--badge-text-color": readableColour(
      [resolvedSecondaryColor, resolvedHeadingTextColor],
      resolvedPrimaryColor,
      1.5,
    ),
  } as React.CSSProperties;

  // Measured fit: if the rendered list overflows its box, first switch to
  // grouped columns (when possible), then scale the list down to fit.
  // transform doesn't change scrollHeight, so the computed scale is stable.
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const content = contentRef.current;
      if (!content) {
        return;
      }

      const overflow = content.scrollHeight - content.clientHeight;
      if (overflow <= 1) {
        return;
      }

      if (canUseColumns && !forceGroupedColumns) {
        setForceGroupedColumns(true);
        return;
      }

      const scale = Math.max(
        0.55,
        content.clientHeight / content.scrollHeight,
      );
      setFitScale((prev) => (Math.abs(prev - scale) > 0.01 ? scale : prev));
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [
    width,
    height,
    canUseColumns,
    forceGroupedColumns,
    useGroupedColumns,
    upcomingChanges,
  ]);

  const layoutClassName = [
    "compact-upcoming-salah-changes",
    useGroupedColumns ? "compact-upcoming-salah-changes--grouped-columns" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const listFitStyle: React.CSSProperties | undefined =
    fitScale < 1
      ? { transform: `scale(${fitScale})`, transformOrigin: "top center" }
      : undefined;

  if (hideWhenNoChanges && upcomingChanges.length === 0) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={layoutClassName}
      style={{
        width: `${width}px`,
        height: `${height}px`,
        position: "relative",
        ...customStyles,
      }}
    >
      <div className="compact-header">
        <h3>Upcoming Salaah Time Changes</h3>
      </div>

      <div ref={contentRef} className="compact-content">
        {upcomingChanges.length === 0 ? (
          <div className="compact-no-changes">
            <p>No upcoming changes</p>
          </div>
        ) : isGroupedData ? (
          // Render grouped changes (DateOrder mode)
          <div
            ref={listRef}
            className="compact-changes-list"
            style={listFitStyle}
          >
            {groupedChanges.map((group, groupIndex) => {
              const groupDate = new Date(group.date);
              const isGroupTomorrow = isTomorrow(groupDate);
              return (
                <div key={groupIndex} className="compact-date-group">
                  <div
                    className={`compact-date-header ${isGroupTomorrow ? "tomorrow" : ""}`}
                  >
                    <span className="day-name">
                      {isGroupTomorrow ? "Tomorrow" : getFullDayName(groupDate)}
                    </span>
                    <span className="short-date">
                      {getShortDate(groupDate)}
                    </span>
                  </div>
                  {group.changes.map((change, changeIndex) => (
                    <div
                      key={`${groupIndex}-${changeIndex}`}
                      className="compact-change-item"
                    >
                      <span className="compact-prayer-name">
                        {change.prayer}
                      </span>
                      <span className="compact-time-badge">
                        {formatPrayerTime(change.time)}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        ) : (
          // Render simple list (SalahOrder mode)
          <div
            ref={listRef}
            className="compact-changes-list"
            style={listFitStyle}
          >
            {prayerChanges.map((change, index) => {
              const changeDate = change.sortDate || new Date(change.date || "");
              const isChangeTomorrow = change.sortDate
                ? isTomorrow(change.sortDate)
                : false;
              return (
                <div
                  key={index}
                  className="compact-change-item salah-order-item"
                >
                  <div className="compact-prayer-info">
                    <span className="compact-prayer-name">{change.prayer}</span>
                    <span
                      className={`compact-date-info ${isChangeTomorrow ? "tomorrow" : ""}`}
                    >
                      {isChangeTomorrow ? "Tomorrow" : formatDate(changeDate)}
                    </span>
                  </div>
                  <span className="compact-time-badge">
                    {formatPrayerTime(change.time)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div className="simple-upcoming-changes-progress">
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{
              width:
                totalDuration && elapsedTime !== undefined
                  ? `${Math.min((elapsedTime / totalDuration) * 100, 100)}%`
                  : "0%",
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default SimpleUpcomingSalahChangesSlideshow;
